'use client'

import { RefreshCw } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useLastSyncRun } from '@/features/admin/hooks/use-admin-users'
import type { LastSyncRun } from '@/features/admin/api/admin-users.api'
import { formatDateTime, formatRelativeTime } from '@/lib/format/date'
import { cn } from '@/lib/utils'

const SOURCE_LABEL: Record<NonNullable<LastSyncRun['source']>, string> = {
  CRON: 'Daily sync',
  STARTUP_CATCHUP: 'Startup catch-up',
  MANUAL: 'Manual sync',
}

/**
 * Whether the Clerk↔database sync is healthy, at a glance. The sync runs on its own (daily, plus a
 * catch-up on startup), so this is the only place an admin would notice it silently failing.
 * Purely informational — never blocks the page, so a failed status request just says so.
 */
export function SyncStatusLine({ className }: { className?: string }) {
  const { data, isLoading, isError } = useLastSyncRun()

  if (isLoading) return null

  let text: string
  let failed = false
  let title: string | undefined

  if (isError || !data) {
    text = 'Sync status unavailable'
  } else if (data.lastRunAt === null || data.source === null) {
    text = 'Not synced yet'
  } else {
    const when = formatRelativeTime(data.lastRunAt)
    failed = data.status === 'FAILED'
    text = `${failed ? 'Last sync failed' : 'Last synced'} ${when} · ${SOURCE_LABEL[data.source]}`
    title = formatDateTime(data.lastRunAt)
  }

  const line = (
    <p
      role="status"
      // Focusable only when there's a tooltip to reveal, so keyboard users can reach it too.
      tabIndex={title ? 0 : undefined}
      className={cn(
        'inline-flex items-center gap-1.5 text-xs',
        failed ? 'text-destructive' : 'text-muted-foreground',
        className,
      )}
    >
      <RefreshCw className="size-3 shrink-0" aria-hidden />
      {text}
    </p>
  )

  // The app's own tooltip (not the browser's native `title`), showing the exact IST run time.
  if (!title) return line
  return (
    <Tooltip>
      <TooltipTrigger asChild>{line}</TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  )
}
